import { ATTR_NAME } from 'constants/attr-name'
import { CLASS_NAME } from 'constants/class-names'
import { EventType, KeyType } from 'constants/enums'
import { DOMUtils } from 'utils/dom'
import { CSSUtils } from 'utils/css'
import { ComponentBase } from 'utils/abstract-classes/component-base'
import { SetAriaExpanded } from 'utils/aria'

const { dropdownChecklist: className } = CLASS_NAME
const { dropdownChecklist: attrs } = ATTR_NAME.main

export class DropdownChecklist extends ComponentBase {
    private fieldset: HTMLFieldSetElement
    private button: HTMLButtonElement
    private checkboxes: HTMLInputElement[]

    public constructor(private wrapper: HTMLDivElement) {
        super()

        const fieldset = this.getElement<HTMLFieldSetElement>(
            `[${attrs.fieldset}]`,
            wrapper
        )

        const button = this.getElement<HTMLButtonElement>(
            `[${attrs.button}]`,
            wrapper
        )

        if (!fieldset || !button) {
            // no dropdown elements, quit
            return
        }

        this.fieldset = fieldset
        this.button = button

        const checkboxes = this.getElements<HTMLInputElement>('input', fieldset)

        if (!checkboxes) {
            // no inputs, quit
            return
        }

        this.checkboxes = checkboxes

        this.fieldset.addEventListener(EventType.KEYDOWN, (e) => {
            this.fieldsetKeyboardControls(e)
        })

        this.button.addEventListener(EventType.CLICK, () => {
            this.toggleButton()
        })

        // Keyboard: enter toggle open, down key focus first checkbox
        this.button.addEventListener(EventType.KEYDOWN, (e) => {
            if (
                e.key == KeyType.DOWN &&
                CSSUtils.checkClass(this.button, className.open)
            ) {
                this.checkboxes[0].focus()
            }

            if (e.key == KeyType.ENTER) {
                this.toggleButton()
            }
        })

        // Keyboard: esc closes dropdown, returns focus to trigger button
        document.addEventListener(EventType.KEYDOWN, (e) => {
            if (
                e.key == KeyType.ESC &&
                CSSUtils.checkClass(this.button, className.open)
            ) {
                this.button.focus()
                this.toggleButton()
            }
        })

        // Click outside target dropdown closes dropdown
        document.addEventListener(EventType.CLICK, (e) => {
            const target = e.target as Element
            if (!this.wrapper.contains(target)) {
                this.toggleButton('close')
            }
        })
    }

    /**
     * Keyboard for inside the dropdown checkbox list
     * Arrow Up, Down goes to nearest sibling in the matching direction
     * Home & End Keys jump to top or bottom of the list
     *
     * @param {KeyboardEvent} event
     */
    private fieldsetKeyboardControls(event: KeyboardEvent): void {
        const index = this.checkboxes.findIndex((item) => item === event.target)

        switch (event.key) {
            case KeyType.DOWN:
                this.skipToSibling(1, index)
                break
            case KeyType.UP:
                this.skipToSibling(-1, index)
                break
            case KeyType.HOME:
                this.checkboxes[0].focus()
                break
            case KeyType.END:
                this.checkboxes[this.checkboxes.length - 1].focus()
                break
            default:
                break
        }
    }

    /**
     * skipToSibling uses direction and current index to set focus on
     * the next or previous checkbox
     *
     * @param {number} direction 1 forwards, -1 backwards
     * @param {number} index index of the current checkbox
     */
    private skipToSibling(direction: number, index: number): void {
        const len = this.checkboxes.length
        const newIndex = index + direction
        const jumpToMax = newIndex < 0 ? len - 1 : newIndex
        const indexLoop = jumpToMax > len - 1 ? 0 : jumpToMax
        this.checkboxes[indexLoop].focus()
    }

    /**
     * toggleButton will reverse the class names and attributes
     * to open or close the dropdown
     * optional to pass state as 'close' to force the dropdown closed
     *
     * @param {string 'close'} state optional
     */
    private toggleButton(state?: 'close'): void {
        if (
            CSSUtils.checkClass(this.button, className.open) ||
            state == 'close'
        ) {
            CSSUtils.removeClass(this.button, className.open)

            SetAriaExpanded(this.button, 'false')
            DOMUtils.setAttr(this.fieldset, 'hidden', '')
        } else {
            CSSUtils.addClass(this.button, className.open)
            SetAriaExpanded(this.button, 'true')
            if (this.fieldset) this.fieldset.removeAttribute('hidden')
        }
    }
}
